import { Badge } from '@polichat/flamboyant';
import { connect, useSelector } from 'react-redux';
import { bindActionCreators } from 'redux';
import * as ChatActions from '../../../../store/modules/chat/actions';
import { dtmToFormatString } from '../../../../utils/dtmToFormatString';
import formatText from 'src/utils/formatText';
import { format } from 'date-fns';
import { translateSystemMessage } from '../../../../utils/translateSystemMessage';
import { useGlotaT } from 'src/hooks/useGlotaTranslation';

const getBodyText = (text) => {
  if (!text) return '';

  try {
    const parsed = JSON.parse(text);
    return parsed?.body?.text || '';
  } catch (e) {
    return '';
  }
};

function SystemMessage({ props, opennedChat }) {
  const timezone = useSelector(
    (state) => state?.general?.user?.settings?.timezone[0]?.zone
  );
  const chat = opennedChat;
  const translateFn = useGlotaT;
  
  let icon = styles({ messageType: props.type }).icon;
  let badge = styles({ messageType: props.type }).badge;
  let content;
  let extraClass;

  // Precarregar traduções necessárias
  const contactCalledText = translateFn('spa.chat.systemMessages.contactCalled');
  const messagesProtectedText = translateFn('spa.chat.systemMessages.messagesProtected');
  const messageDeletedText = translateFn('spa.chat.systemMessages.messageDeleted');
  const summarizedWithAIText = translateFn('spa.chat.systemMessages.summarizedWithAI');

  /**
   * Estilização da mensagem tipo call_log (chamada perdida)
   */
  if (props.type === 'call_log') {
    icon = {
      ...icon,
      color: 'var(--alert-system-red)',
      icon: 'poli-icon pi-smartphone-fill',
    };

    badge = {
      ...badge,
      color: 'var(--alert-system-red)',
      backgroundColor: 'var(--alert-system-red-background)',
      border: 'var(--alert-system-red-border)',
    };

    content = contactCalledText;
  }

  /**
   * Estilização da mensagem tipo Chat Encerrado
   */
  if (props.type === 'sys_cfw_closed' && chat) {
    badge = {
      ...badge,
      color: 'var(--primary-background-color)',
      backgroundColor: 'var(--red-polichat)',
      border: 'var(--alert-system-red-border)',
    };

    extraClass = 'finishedAt';

    // Adiciona o horário de envio da mensagem quando tiver sido finalizado pela URA
    if (
      props &&
      !props.userId && // mensagens do tipo sys-cfw-closed sem userId só ocorrem quando o chat é finalizado pela URA
      props.text &&
      !/\d:\d\d/.test(props.text.slice(-4)) // checa se o final da mensagem é do tipo X:XX
    ) {
      // adiciona espaço no final caso não tenha
      if (props.text.slice(props.text.length - 1) != ' ') props.text += ' ';

      props.text += dtmToFormatString(props.dtm, timezone)
        .slice(0, -3) // removes seconds
        .split(' ')
        .join(' às '); // retorna "dd/mm/yy às hh:mm"
    }
  }

  /**
   * Estilização da mensagem tipo e2e (criptografia e segurança whatsapp)
   */
  if (props.type === 'e2e_notification') {
    icon = {
      ...icon,
      color: 'var(--alert-system-green)',
      icon: 'poli-icon pi-password-fill',
    };

    badge = {
      ...badge,
      color: 'var(--alert-system-green)',
      backgroundColor: 'var(--alert-system-green-background)',
      border: 'var(--alert-system-green-border)',
    };

    content = messagesProtectedText;
  }
  /**
   * Estilização da mensagem tipo revoked (mensagem excluida pelo contato)
   */
  if (props.type === 'revoked') {
    icon = {
      ...icon,
      color: 'var(--alert-system-red)',
      icon: 'poli-icon pi-delete-fill',
    };

    badge = {
      ...badge,
      color: 'var(--alert-system-red)',
      backgroundColor: 'var(--alert-system-red-background)',
      border: 'var(--alert-system-red-border)',
    };

    content = messageDeletedText;
  }
  /**
   * Estilização da mensagem tipo oversized (mensagem excede limite de caracteres do whatsapp)
   */
  if (props.type === 'oversized') {
    icon = {
      ...icon,
      color: 'var( --alert-system-orange)',
      icon: 'poli-icon pi-alert-fill',
    };

    badge = {
      ...badge,
      color: 'var( --alert-system-orange)',
      backgroundColor: 'var( --alert-system-orange-background)',
      border: 'var( --alert-system-orange-border)',
    };

    content = messageDeletedText;
  }

  if (props.type === 'sys_cfw') {
    icon = {
      ...icon,
      icon: 'poli-icon pi-auto-talks-line',
    };
  }

  const handleText = (text) => {
    if (!text) return false;
    const bodyText = getBodyText(text) || text.replace(/\[bot]/i, '');
    // Aplica tradução para mensagens de sistema, passando dtm e timezone para substituir datas
    return translateSystemMessage(bodyText, translateFn, { dtm: props.dtm, timezone });
  };

  const isSummary =
    props?.type === 'summary' ||
    props?.type === 'summary_loading' ||
    props?.type === 'summary_error';

  return (
    <Badge
      style={badge}
      className={`systemMessageContainer ${extraClass || ''} ${
        isSummary && 'text-start'
      }`}
    >
      {isSummary && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
            textAlign: 'start',
            fontSize: '1rem',
            fontWeight: '600',
            lineHeight: '120%',
          }}
        >
          <div className="w-full text-start text-gradient">
            <span>
              {summarizedWithAIText}{' '}
              {format(new Date(Number(props.dtm * 1000)), 'dd/MM/yyyy')}:{' '}
            </span>
          </div>

          <hr
            style={{
              margin: 0,
              borderColor: '#fff',
            }}
          />

          <span
            className={`
              ${props?.type === 'summary' ? 'text-gradient' : ''}
              ${
                props?.type === 'summary_loading'
                  ? 'text-gradient-black summary-loading'
                  : ''
              }
            `}
            style={{
              color: 'var(--gray-dark-75, var(--gray-dark, #5a677e))',
            }}
            dangerouslySetInnerHTML={{
              __html: handleText(props.text),
            }}
          />
        </div>
      )}
      {!isSummary && <span>{handleText(props.text) || content}</span>}
    </Badge>
  );
}

const styles = ({ messageType = '' }) => ({
  badge: {
    border: 'var(--alert-system-blue-border)',
    backgroundColor: `${
      messageType.includes('summary')
        ? 'var(--received-message-background)'
        : 'var(--alert-system-blue-background)'
    }`,
    color: 'var(--system-font-color)',
    margin: '0.5em',
    padding: '0.7em',
    width: '100%',
    fontSize: '0.8rem',
    whiteSpace: 'break-spaces',
  },

  icon: {
    color: 'var(--alert-system-blue)',
  },

  summaryLoading: {
    color: 'red',
  },
});

const mapStateToProps = (state, ownProps) => ({
  props: ownProps,
  opennedChat:
    state.chat?.chats?.filter((chat) => {
      return chat.open === true;
    })[0] || [],
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(ChatActions, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(SystemMessage);
